import PropTypes from 'prop-types';
import { useMemo } from 'react';
import Controls from './Controls';
import ContactList from '../../../ContactList';
import useSearch from '../../../../hooks/useSearch';
import { useSelectableUserList } from '../../../../hooks/useSelectableUserList';
import { useI18n } from '../../../../context/I18nContext';
import './SideBar.css';

const SideBar = ({
  contacts = [],
  setCurrentContactId = () => {},
  selectedContactId = null,
  onMenuToggle = () => {},
  isLoading = false,
}) => {
  const { t } = useI18n();
  const {
    searchTerm: localSearchTerm,
    setSearchTerm: setLocalSearchTerm,
    isSearching,
    setIsSearching,
    cancelSearch,
  } = useSearch();

  // Use unified selectable user list (known contacts + search results)
  const { users: searchableUsers, searchTerm, setSearchTerm } = useSelectableUserList();

  // Sync search terms between local state and the hook
  const handleSearchTermChange = (term) => {
    setLocalSearchTerm(term);
    setSearchTerm(term);
  };

  // When searching, use searchableUsers (includes directory search)
  // When not searching, use local contacts
  const filteredContacts = useMemo(() => {
    if (!isSearching || !searchTerm.trim()) {
      // No search active: return local contacts filtered by term
      return contacts.filter((contact) => 
        contact.name.toLowerCase().includes(localSearchTerm.toLowerCase()),
      );
    }
    
    // Search active: use searchableUsers from useSelectableUserList
    // These are already filtered by the hook
    return searchableUsers;
  }, [isSearching, searchTerm, localSearchTerm, contacts, searchableUsers]);

  return (
    <div className="side-bar">
      <div className={`side-bar-header ${(isSearching) ? 'searching' : ''}`}>
        <Controls
          searchTerm={localSearchTerm}
          isSearching={isSearching}
          setIsSearching={setIsSearching}
          setSearchTerm={handleSearchTermChange}
          cancelSearch={cancelSearch}
          onMenuToggle={onMenuToggle}
        />
        {
          !isSearching && <div className="side-bar-tabs"></div>
        }
      </div>
      <div className={`contact-list-container ${(isSearching) ? 'searching' : ''}`}>
        <ContactList
          contacts={filteredContacts}
          selectedContactId={selectedContactId}
          onSelectContact={setCurrentContactId}
          emptyText={isLoading ? '' : t('Contacts.noContactsFound')}
        />
      </div>
    </div>
  );
};

SideBar.propTypes = {
  contacts: PropTypes.array,
  setCurrentContactId: PropTypes.func,
  selectedContactId: PropTypes.string,
  onMenuToggle: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default SideBar;
